/**
 * BillingContext (R0.3)
 *
 * Manages the tenant write-block state with two sources of truth:
 *  1. Proactive: computed from billing snapshot received on login/refresh.
 *  2. Reactive: set to true when the Axios interceptor receives 403 TENANT_WRITE_BLOCKED.
 *
 * The module-level bridge (registerBillingWriteBlockCallback / signalWriteBlocked)
 * allows the Axios interceptor (which lives outside React) to trigger a state update.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import type { BillingSnapshot } from './AuthContext';

// ─── Module-level bridge for Axios interceptor ────────────────────────────────

let _onWriteBlocked: (() => void) | null = null;

export function registerBillingWriteBlockCallback(fn: () => void): void {
  _onWriteBlocked = fn;
}

export function signalWriteBlocked(): void {
  _onWriteBlocked?.();
}

// ─── Write-block computation ──────────────────────────────────────────────────

function computeIsWriteBlocked(snapshot: BillingSnapshot | null): boolean {
  if (!snapshot) return false;
  const { billingStatus, trialEndDate, subscriptionEndDate } = snapshot;

  if (billingStatus === 'EXPIRED' || billingStatus === 'SUSPENDED' || billingStatus === 'CANCELLED') {
    return true;
  }

  const today = new Date().toISOString().split('T')[0];

  if (billingStatus === 'TRIAL' && trialEndDate && trialEndDate < today) {
    return true;
  }

  if (
    (billingStatus === 'ACTIVE' || billingStatus === 'TRIAL') &&
    subscriptionEndDate &&
    subscriptionEndDate < today
  ) {
    return true;
  }

  return false;
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface BillingContextValue {
  isWriteBlocked: boolean;
}

const BillingContext = createContext<BillingContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { user, billingSnapshot } = useAuth();
  const prevTenantIdRef = useRef<string | null>(null);
  const [isWriteBlocked, setIsWriteBlocked] = useState(false);

  // Recompute whenever billing snapshot changes (login / refresh / logout)
  useEffect(() => {
    const currentTenantId = user?.tenantId ?? null;
    // On tenant change (login / logout), always recompute from scratch
    if (currentTenantId !== prevTenantIdRef.current) {
      prevTenantIdRef.current = currentTenantId;
      setIsWriteBlocked(computeIsWriteBlocked(billingSnapshot));
    } else {
      // Same tenant: proactive snapshot update (only turns block ON, never OFF proactively
      // — the reset happens on the tenant-change branch above)
      if (computeIsWriteBlocked(billingSnapshot)) {
        setIsWriteBlocked(true);
      }
    }
  }, [billingSnapshot, user?.tenantId]);

  // Register the Axios bridge callback once
  const onWriteBlocked = useCallback(() => {
    setIsWriteBlocked(true);
  }, []);

  useEffect(() => {
    registerBillingWriteBlockCallback(onWriteBlocked);
  }, [onWriteBlocked]);

  return (
    <BillingContext.Provider value={{ isWriteBlocked }}>
      {children}
    </BillingContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBilling(): BillingContextValue {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be inside BillingProvider');
  return ctx;
}
